import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthenticationGuard, canActivateRoleRoute } from '@thehubz/client/core';
import { Asset } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AssetAccessRole, NotRole } from '@thehubz/shared/user-access';
import { AssetDataComponent } from './asset-data/asset-data.component';
import { AssetDetailViewComponent } from './asset-detail-view.component';
import { AssetDownloadHistoryComponent } from './asset-download-history/asset-download-history.component';
import { AssetHistoryComponent } from './asset-history/asset-history.component';
import { AssetInfoComponent } from './asset-info/asset-info.component';
import { AssetProductionDetailsComponent } from './asset-production-details/asset-production-details.component';
import { AssetRelationsComponent } from './asset-relations/asset-relations.component';
import { AssetTaggingNTaxonomyComponent } from './asset-tagging-n-taxonomy/asset-tagging-n-taxonomy.component';
import { AssetDetailsBuyoutRightsComponent } from './asset-usage-rights/asset-details-buyout-rights/asset-details-buyout-rights.component';
import { AssetUsageRightsComponent } from './asset-usage-rights/asset-usage-rights.component';
import { AssetVariantsComponent } from './asset-variants/asset-variants.component';
import { AssetVersionsComponent } from './asset-versions/asset-versions.component';

export enum AssetsDetailViewRoutingPaths {
  AssetInfo = 'asset-info',
  AssetData = 'asset-data',
  AssetUsageRights = 'asset-usage-rights',
  AssetBuyoutRights = 'asset-buyout-rights',
  AssetTagging = 'asset-tagging-n-taxonomy',
  AssetProductionDetails = 'asset-production-details',
  AssetRelations = 'asset-relations',
  AssetVersions = 'asset-versions',
  AssetAspects = 'asset-aspect-ratios',
  AssetDownloads = 'asset-downloads',
  AssetHistory = 'asset-history',
}

const routes: Routes = [
  {
    path: '',
    component: AssetDetailViewComponent,
    canActivate: [AuthenticationGuard],
    children: [
      { path: '', redirectTo: AssetsDetailViewRoutingPaths.AssetInfo, pathMatch: 'full' },
      {
        path: AssetsDetailViewRoutingPaths.AssetInfo,
        canActivate: [canActivateRoleRoute],
        data: { label: $localize`:Asset tab:General info` },
        component: AssetInfoComponent,
      },
      {
        path: AssetsDetailViewRoutingPaths.AssetData,
        canActivate: [canActivateRoleRoute],
        data: { label: $localize`:Asset tab:Asset data`, role: [AssetAccessRole.tabAssetData, NotRole(AssetAccessRole.infoOnly)] },
        component: AssetDataComponent,
      },
      {
        path: AssetsDetailViewRoutingPaths.AssetUsageRights,
        canActivate: [canActivateRoleRoute],
        data: {
          label: $localize`:Asset tab:Usage rights`,
          role: [AssetAccessRole.tabUsageRight, NotRole(AssetAccessRole.infoOnly)],
          customClients: [CustomClients.Arval, CustomClients.Allianz, CustomClients.VKB],
        },
        component: AssetUsageRightsComponent,
      },
      {
        path: AssetsDetailViewRoutingPaths.AssetBuyoutRights,
        canActivate: [canActivateRoleRoute],
        data: {
          label: $localize`:Asset tab:Usage rights`,
          role: [AssetAccessRole.tabUsageRight, NotRole(AssetAccessRole.infoOnly)],
          customClients: [CustomClients.Vorwerk, CustomClients.Standard, CustomClients.SEAT],
        },
        component: AssetDetailsBuyoutRightsComponent,
      },
      {
        path: AssetsDetailViewRoutingPaths.AssetTagging,
        canActivate: [canActivateRoleRoute],
        data: {
          label: $localize`:Asset tab:Tagging & taxonomy`,
          alternateLabel: $localize`:Asset tab:Tags`,
          role: [AssetAccessRole.tabTags, NotRole(AssetAccessRole.infoOnly)],
        },
        component: AssetTaggingNTaxonomyComponent,
      },
      {
        path: AssetsDetailViewRoutingPaths.AssetProductionDetails,
        canActivate: [canActivateRoleRoute],
        data: {
          label: $localize`:Asset tab:Production`,
          alternateLabel: $localize`:Asset tab:Production`,
          role: [AssetAccessRole.tabProductionDetails, NotRole(AssetAccessRole.infoOnly)],
        },
        component: AssetProductionDetailsComponent,
      },
      {
        path: AssetsDetailViewRoutingPaths.AssetRelations,
        canActivate: [canActivateRoleRoute],
        data: { label: $localize`:Asset tab:Relations`, role: [AssetAccessRole.tabRelations, NotRole(AssetAccessRole.infoOnly)] },
        component: AssetRelationsComponent,
      },
      {
        path: AssetsDetailViewRoutingPaths.AssetVersions,
        canActivate: [canActivateRoleRoute],
        data: { label: $localize`:Asset tab:Versions`, role: [AssetAccessRole.tabVersion, NotRole(AssetAccessRole.infoOnly)] },
        component: AssetVersionsComponent,
      },
      {
        path: AssetsDetailViewRoutingPaths.AssetAspects,
        canActivate: [canActivateRoleRoute],
        data: {
          label: (asset?: Asset, brandingKey?: CustomClients) => {
            if (brandingKey === CustomClients.WestLotto) {
              return 'Formate';
            }
            if (asset?.assetType === Asset.AssetTypeEnum.Audio) {
              return $localize`:Asset tab:Formats`;
            }
            return $localize`:Asset tab:Aspect ratios`;
          },
          excludeAssetTypes: [Asset.AssetTypeEnum.Zip, Asset.AssetTypeEnum.Other],
          role: [AssetAccessRole.tabAspectRatio, NotRole(AssetAccessRole.infoOnly)],
        },
        component: AssetVariantsComponent,
      },
      {
        path: AssetsDetailViewRoutingPaths.AssetDownloads,
        canActivate: [canActivateRoleRoute],
        data: { label: $localize`:Asset tab:Downloads`, roles: [AssetAccessRole.tabDownload, NotRole(AssetAccessRole.infoOnly)] },
        component: AssetDownloadHistoryComponent,
      },
      {
        path: AssetsDetailViewRoutingPaths.AssetHistory,
        canActivate: [canActivateRoleRoute],
        data: { label: $localize`:Asset tab:History`, roles: [AssetAccessRole.tabHistory, NotRole(AssetAccessRole.infoOnly)] },
        component: AssetHistoryComponent,
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class AssetsDetailViewRoutingModule {}
